'use client';

import canonical from '@segment/canonical';
import url from 'component-url';
import { useEffect } from 'react';

import {
  type TreatmentCategory,
  TreatmentCategoryIds,
} from '@/consts/treatmentCategories';
import { getEventFlowValueFromVisitType } from '@/helpers/visitUtils';
import { type Visit } from '@/services/visitsService/types';
import { trackEvent, AnalyticsEvent } from '@/utils/analytics';

const canonicalPath = (): string => {
  const canon = canonical();

  if (!canon) {
    return window.location.pathname;
  }

  const parsed = url.parse(canon);
  return parsed.pathname;
};

const canonicalUrl = (search: string): string => {
  const canon = canonical();

  if (canon) {
    return canon.indexOf('?') !== -1 ? canon : canon + search;
  }

  const uri = window.location.href;
  const index = uri.indexOf('#');
  return index === -1 ? uri : uri.slice(0, index);
};

// see https://segment.com/docs/spec/page/#properties
// and https://github.com/segmentio/analytics.js-core/blob/2facc5f327b84513ebbede2e5392483ea654f117/lib/pageDefaults.js
const pageDefaults = (
  currentUrl: string = window.location.href,
): {
  path: string;
  referrer: string;
  search: string;
  url: string;
  host: string;
} => {
  const url = new URL(currentUrl);
  return {
    path: canonicalPath(),
    referrer: document.referrer,
    search: url.search,
    url: canonicalUrl(url.search),
    host: url.hostname,
  };
};

const useTrackPageEffect = ({
  title,
  treatmentCategory,
  currentUrl,
  visit,
}: {
  title: string;
  treatmentCategory?: TreatmentCategory;
  currentUrl?: string;
  visit?: Visit;
}) => {
  useEffect(() => {
    const treatmentCategoryIds = treatmentCategory
      ? TreatmentCategoryIds[treatmentCategory]
      : undefined;

    trackEvent(AnalyticsEvent.PAGE_LOADED, {
      ...pageDefaults(currentUrl),
      flow: getEventFlowValueFromVisitType(visit),
      name: title,
      treatmentCategories: treatmentCategory,
      treatmentCategoryIds,
    });
  }, [currentUrl, title, treatmentCategory, visit]);
};

export { useTrackPageEffect, canonicalPath };
